import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
      
      <div className="prose prose-blue max-w-none">
        <p className="text-sm text-gray-500 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
          <p className="text-gray-700 mb-4">
            D-ESPIAL Security Platform ("we," "our," or "us") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our security intelligence platform and services.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">Personal Information</h3>
          <p className="text-gray-700 mb-4">
            We collect information you provide directly to us:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li>Name and email address</li>
            <li>Organization name (optional)</li>
            <li>Account credentials</li>
            <li>Location data for security assessments (when provided)</li>
            <li>Communication preferences</li>
          </ul>

          <h3 className="text-lg font-medium text-gray-900 mb-2">Usage Information</h3>
          <p className="text-gray-700 mb-4">
            We automatically collect certain information when you use our Service:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li>Log data (IP address, browser type, pages visited)</li>
            <li>Device information</li>
            <li>Usage patterns and preferences</li>
            <li>Report access history</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Payment Processing & Stripe</h2>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <p className="text-gray-700">
              <strong>Payment Processor:</strong> We use <strong>Stripe, Inc.</strong> as our third-party payment processor 
              to handle all payment transactions securely.
            </p>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">Payment Data Handling</h3>
          <p className="text-gray-700 mb-4">
            When you make a payment:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li><strong>We do NOT store</strong> your full credit card number, CVV, or other sensitive payment credentials on our servers</li>
            <li>Payment information is transmitted directly to Stripe via encrypted connections</li>
            <li>We only store a reference ID (Stripe Customer ID) to manage your subscription</li>
            <li>Stripe may store your payment method details securely in accordance with their privacy policy</li>
          </ul>
          
          <p className="text-gray-700 mb-4">
            For more information about how Stripe handles your data, please review 
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500"> Stripe's Privacy Policy</a>.
          </p>

          <h3 className="text-lg font-medium text-gray-900 mb-2">PCI DSS Compliance</h3>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <p className="text-gray-700">
              <strong>PCI Compliance Statement:</strong> Our payment processing is handled by Stripe, which is a 
              certified PCI Level 1 Service Provider—the most stringent level of certification in the payment card industry. 
              This means your payment data is protected by industry-leading security standards including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>256-bit SSL encryption for all data transmission</li>
              <li>Secure, PCI-compliant data storage</li>
              <li>Regular security audits and penetration testing</li>
              <li>Compliance with global payment security standards</li>
            </ul>
          </div>
          <p className="text-gray-700 mb-4">
            By using Stripe, we ensure that sensitive payment information never touches our servers, 
            minimizing risk and maintaining the highest standards of payment security.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">4. How We Use Your Information</h2>
          <p className="text-gray-700 mb-4">
            We use the information we collect to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li>Provide, maintain, and improve our services</li>
            <li>Process subscriptions and payments</li>
            <li>Send security reports and alerts</li>
            <li>Respond to your inquiries and support requests</li>
            <li>Send administrative communications (billing, account updates)</li>
            <li>Analyze usage patterns to improve user experience</li>
            <li>Protect against fraud and unauthorized access</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Information Sharing</h2>
          <p className="text-gray-700 mb-4">
            We do not sell your personal information. We may share your information with:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li><strong>Service Providers:</strong> Third parties that help us operate our business (Stripe for payments, Supabase for data storage)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
          <p className="text-gray-700 mb-4">
            We implement appropriate technical and organizational measures to protect your information:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li>Encryption of data in transit (TLS/SSL)</li>
            <li>Encryption of data at rest</li>
            <li>Regular security assessments</li>
            <li>Access controls and authentication</li>
            <li>Employee training on data protection</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
          <p className="text-gray-700 mb-4">
            We retain your information for as long as your account is active or as needed to provide services. 
            After account deletion:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li>Personal data is deleted within 30 days</li>
            <li>Anonymized usage data may be retained for analytics</li>
            <li>Transaction records are retained as required by law (typically 7 years)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Your Rights</h2>
          <p className="text-gray-700 mb-4">
            You have the right to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Portability:</strong> Receive your data in a portable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
          </ul>
          <p className="text-gray-700 mb-4">
            To exercise these rights, contact us at support@d-espial.com or visit your <Link to="/profile" className="text-blue-600 hover:text-blue-500">account settings</Link>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Cookies and Tracking</h2>
          <p className="text-gray-700 mb-4">
            We use cookies and similar technologies to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li>Maintain your session and authentication</li>
            <li>Remember your preferences</li>
            <li>Analyze site usage</li>
          </ul>
          <p className="text-gray-700 mb-4">
            You can control cookies through your browser settings, though this may affect site functionality.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
          <p className="text-gray-700 mb-4">
            Your information may be transferred to and processed in countries other than your own. 
            We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Children's Privacy</h2>
          <p className="text-gray-700 mb-4">
            Our Service is not intended for children under 18. We do not knowingly collect personal information 
            from children. If we become aware of such collection, we will delete the information promptly.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Changes to This Policy</h2>
          <p className="text-gray-700 mb-4">
            We may update this Privacy Policy from time to time. We will notify you of material changes 
            by email or through the Service. Your continued use after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
          <p className="text-gray-700 mb-4">
            For questions about this Privacy Policy or our data practices:
          </p>
          <ul className="list-none text-gray-700 mb-4 space-y-1">
            <li>Email: privacy@d-espial.com</li>
            <li>Support: support@d-espial.com</li>
            <li>Visit our <Link to="/contact" className="text-blue-600 hover:text-blue-500">Contact Page</Link></li>
          </ul>
        </section>

        <div className="border-t border-gray-200 pt-8 mt-8">
          <p className="text-sm text-gray-500">
            By using our Service, you acknowledge that you have read and understood this Privacy Policy.
          </p>
          <div className="mt-4 flex space-x-4">
            <Link to="/terms" className="text-blue-600 hover:text-blue-500 text-sm">Terms of Service</Link>
            <Link to="/contact" className="text-blue-600 hover:text-blue-500 text-sm">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
